/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  Optional,
  Output,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  NgZone,
  Inject,
} from '@angular/core';
import {DOCUMENT} from '@angular/platform-browser';
import {Overlay} from '../core/overlay/overlay';
import {OverlayRef} from '../core/overlay/overlay-ref';
import {ComponentPortal} from '../core/portal/portal';
import {OverlayState} from '../core/overlay/overlay-state';
import {Dir} from '../core/rtl/dir';
import {MdDialog} from '../dialog/dialog';
import {MdDialogRef} from '../dialog/dialog-ref';
import {PositionStrategy} from '../core/overlay/position/position-strategy';
import {MdDatepickerInput} from './datepicker-input';
import {Subscription} from 'rxjs/Subscription';
import {MdDialogConfig} from '../dialog/dialog-config';
import {DateAdapter} from '../core/datetime/index';
import {createMissingDateImplError} from './datepicker-errors';
import {ESCAPE} from '../core/keyboard/keycodes';
import {MdCalendar} from './calendar';
import'rxjs/add/operator/first';
/**
 * Used to generate a unique ID for each datepicker instance.
 */
let datepickerUid = 0;
/**
 * Component used as the content for the datepicker dialog and popup. We use this instead of using
 * MdCalendar directly as the content so we can control the initial focus. This also gives us a
 * place to put additional features of the popup that are not part of the calendar itself in the
 * future. (e.g. confirmation buttons).
 * \@docs-private
 */
export class MdDatepickerContent<D> implements AfterContentInit {
  datepicker: MdDatepicker<D>;

   _calendar: MdCalendar<D>;
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._calendar._focusActiveCell();
  }
/**
 * Handles keydown event on datepicker content.
 * @param {?} event The event.
 * @return {?}
 */
_handleKeydown(event: KeyboardEvent): void {
    if (event.keyCode === ESCAPE) {
      this.datepicker.close();
      event.preventDefault();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-datepicker-content',
  templateUrl: 'datepicker-content.html',
  styleUrls: ['datepicker-content.css'],
  host: {
    'class': 'mat-datepicker-content',
    '[class.mat-datepicker-content-touch]': 'datepicker.touchUi',
    '(keydown)': '_handleKeydown($event)',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_calendar': [{ type: ViewChild, args: [MdCalendar, ] },],
};
}

function MdDatepickerContent_tsickle_Closure_declarations() {
/** @type {?} */
MdDatepickerContent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdDatepickerContent.ctorParameters;
/** @type {?} */
MdDatepickerContent.propDecorators;
/** @type {?} */
MdDatepickerContent.prototype.datepicker;
/** @type {?} */
MdDatepickerContent.prototype._calendar;
}

/**
 * Component responsible for managing the datepicker popup/dialog.
 */
export class MdDatepicker<D> implements OnDestroy {
/**
 * The date to open the calendar to initially.
 * @return {?}
 */
get startAt(): D {
    // If an explicit startAt is set we start there, otherwise we start at whatever the currently
    // selected value is.
    return this._startAt || (this._datepickerInput ? this._datepickerInput.value : null);
  }
/**
 * @param {?} date
 * @return {?}
 */
set startAt(date: D) { this._startAt = date; }
private _startAt: D;
/**
 * The view that the calendar should start in.
 */
startView: 'month' | 'year' = 'month';
/**
 * Whether the calendar UI is in touch mode. In touch mode the calendar opens in a dialog rather
 * than a popup and elements have more padding to allow for bigger touch targets.
 */
touchUi = false;
/**
 * Emits new selected date when selected date changes.
 */
selectedChanged = new EventEmitter<D>();
/**
 * Whether the calendar is open.
 */
opened = false;
/**
 * The id for the datepicker calendar.
 */
id = `md-datepicker-${datepickerUid++}`;
/**
 * The currently selected date.
 */
_selected: D = null;
/**
 * The minimum selectable date.
 * @return {?}
 */
get _minDate(): D {
    return this._datepickerInput && this._datepickerInput.min;
  }
/**
 * The maximum selectable date.
 * @return {?}
 */
get _maxDate(): D {
    return this._datepickerInput && this._datepickerInput.max;
  }
/**
 * @return {?}
 */
get _dateFilter(): (date: D | null) => boolean {
    return this._datepickerInput && this._datepickerInput._dateFilter;
  }
/**
 * A reference to the overlay when the calendar is opened as a popup.
 */
private _popupRef: OverlayRef;
/**
 * A reference to the dialog when the calendar is opened as a dialog.
 */
private _dialogRef: MdDialogRef<any>;
/**
 * A portal containing the calendar for this datepicker.
 */
private _calendarPortal: ComponentPortal<MdDatepickerContent<D>>;
/**
 * The input element this datepicker is associated with.
 */
private _datepickerInput: MdDatepickerInput<D>;
/**
 * The element that was focused before the datepicker was opened.
 */
private _focusedElementBeforeOpen: HTMLElement;
private _inputSubscription: Subscription;
/**
 * @param {?} _dialog
 * @param {?} _overlay
 * @param {?} _ngZone
 * @param {?} _viewContainerRef
 * @param {?} _dateAdapter
 * @param {?} _dir
 * @param {?} _document
 */
constructor(private _dialog: MdDialog,
private _overlay: Overlay,
private _ngZone: NgZone,
private _viewContainerRef: ViewContainerRef,
private _dateAdapter: DateAdapter<D>,
private _dir: Dir,
private _document: any) {

    if (!this._dateAdapter) {
      throw createMissingDateImplError('DateAdapter');
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this.close();
    if (this._popupRef) {
      this._popupRef.dispose();
    }
    if (this._inputSubscription) {
      this._inputSubscription.unsubscribe();
    }
  }
/**
 * Selects the given date and closes the currently open popup or dialog.
 * @param {?} date
 * @return {?}
 */
_selectAndClose(date: D): void {
    let /** @type {?} */ oldValue = this._selected;
    this._selected = date;
    if (!this._dateAdapter.sameDate(oldValue, this._selected)) {
      this.selectedChanged.emit(date);
    }
    this.close();
  }
/**
 * Register an input with this datepicker.
 * @param {?} input The datepicker input to register with this datepicker.
 * @return {?}
 */
_registerInput(input: MdDatepickerInput<D>): void {
    if (this._datepickerInput) {
      throw Error('An MdDatepicker can only be associated with a single input.');
    }
    this._datepickerInput = input;
    this._inputSubscription =
        this._datepickerInput._valueChange.subscribe((value: D) => this._selected = value);
  }
/**
 * Open the calendar.
 * @return {?}
 */
open(): void {
    if (this.opened) {
      return;
    }
    if (!this._datepickerInput) {
      throw Error('Attempted to open an MdDatepicker with no associated input.');
    }
    if (this._document) {
      this._focusedElementBeforeOpen = this._document.activeElement;
    }

    this.touchUi ? this._openAsDialog() : this._openAsPopup();
    this.opened = true;
  }
/**
 * Close the calendar.
 * @return {?}
 */
close(): void {
    if (!this.opened) {
      return;
    }
    if (this._popupRef && this._popupRef.hasAttached()) {
      this._popupRef.detach();
    }
    if (this._dialogRef) {
      this._dialogRef.close();
      this._dialogRef = null;
    }
    if (this._calendarPortal && this._calendarPortal.isAttached) {
      this._calendarPortal.detach();
    }
    if (this._focusedElementBeforeOpen && 'focus' in this._focusedElementBeforeOpen) {
      this._focusedElementBeforeOpen.focus();
      this._focusedElementBeforeOpen = null;
    }

    this.opened = false;
  }
/**
 * Open the calendar as a dialog.
 * @return {?}
 */
private _openAsDialog(): void {
    let /** @type {?} */ config = new MdDialogConfig();
    config.viewContainerRef = this._viewContainerRef;

    this._dialogRef = this._dialog.open(MdDatepickerContent, config);
    this._dialogRef.afterClosed().subscribe(() => this.close());
    this._dialogRef.componentInstance.datepicker = this;
  }
/**
 * Open the calendar as a popup.
 * @return {?}
 */
private _openAsPopup(): void {
    if (!this._calendarPortal) {
      this._calendarPortal = new ComponentPortal(MdDatepickerContent, this._viewContainerRef);
    }

    if (!this._popupRef) {
      this._createPopup();
    }

    if (!this._popupRef.hasAttached()) {
      let /** @type {?} */ componentRef: ComponentRef<MdDatepickerContent<D>> =
          this._popupRef.attach(this._calendarPortal);
      componentRef.instance.datepicker = this;

      // Update the position once the calendar has rendered.
      this._ngZone.onStable.first().subscribe(() => this._popupRef.updatePosition());
    }

    this._popupRef.backdropClick().subscribe(() => this.close());
  }
/**
 * Create the popup.
 * @return {?}
 */
private _createPopup(): void {
    const /** @type {?} */ overlayState = new OverlayState();
    overlayState.positionStrategy = this._createPopupPositionStrategy();
    overlayState.hasBackdrop = true;
    overlayState.backdropClass = 'md-overlay-transparent-backdrop';
    overlayState.direction = this._dir ? this._dir.value : 'ltr';
    overlayState.scrollStrategy = this._overlay.scrollStrategies.reposition();

    this._popupRef = this._overlay.create(overlayState);
  }
/**
 * Create the popup PositionStrategy.
 * @return {?}
 */
private _createPopupPositionStrategy(): PositionStrategy {
    return this._overlay.position()
      .connectedTo(this._datepickerInput.getPopupConnectionElementRef(),
        {originX: 'start', originY: 'bottom'},
        {overlayX: 'start', overlayY: 'top'}
      )
      .withFallbackPosition(
        { originX: 'start', originY: 'top' },
        { overlayX: 'start', overlayY: 'bottom' }
      )
      .withFallbackPosition(
        {originX: 'end', originY: 'bottom'},
        {overlayX: 'end', overlayY: 'top'}
      )
      .withFallbackPosition(
        { originX: 'end', originY: 'top' },
        { overlayX: 'end', overlayY: 'bottom' }
      );
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-datepicker, mat-datepicker',
  template: '',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdDialog, },
{type: Overlay, },
{type: NgZone, },
{type: ViewContainerRef, },
{type: DateAdapter, decorators: [{ type: Optional }, ]},
{type: Dir, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [DOCUMENT, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'startAt': [{ type: Input },],
'startView': [{ type: Input },],
'touchUi': [{ type: Input },],
'selectedChanged': [{ type: Output },],
};
}

function MdDatepicker_tsickle_Closure_declarations() {
/** @type {?} */
MdDatepicker.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdDatepicker.ctorParameters;
/** @type {?} */
MdDatepicker.propDecorators;
/** @type {?} */
MdDatepicker.prototype._startAt;
/**
 * The view that the calendar should start in.
 * @type {?}
 */
MdDatepicker.prototype.startView;
/**
 * Whether the calendar UI is in touch mode. In touch mode the calendar opens in a dialog rather
 * than a popup and elements have more padding to allow for bigger touch targets.
 * @type {?}
 */
MdDatepicker.prototype.touchUi;
/**
 * Emits new selected date when selected date changes.
 * @type {?}
 */
MdDatepicker.prototype.selectedChanged;
/**
 * Whether the calendar is open.
 * @type {?}
 */
MdDatepicker.prototype.opened;
/**
 * The id for the datepicker calendar.
 * @type {?}
 */
MdDatepicker.prototype.id;
/**
 * The currently selected date.
 * @type {?}
 */
MdDatepicker.prototype._selected;
/**
 * A reference to the overlay when the calendar is opened as a popup.
 * @type {?}
 */
MdDatepicker.prototype._popupRef;
/**
 * A reference to the dialog when the calendar is opened as a dialog.
 * @type {?}
 */
MdDatepicker.prototype._dialogRef;
/**
 * A portal containing the calendar for this datepicker.
 * @type {?}
 */
MdDatepicker.prototype._calendarPortal;
/**
 * The input element this datepicker is associated with.
 * @type {?}
 */
MdDatepicker.prototype._datepickerInput;
/**
 * The element that was focused before the datepicker was opened.
 * @type {?}
 */
MdDatepicker.prototype._focusedElementBeforeOpen;
/** @type {?} */
MdDatepicker.prototype._inputSubscription;
/** @type {?} */
MdDatepicker.prototype._dialog;
/** @type {?} */
MdDatepicker.prototype._overlay;
/** @type {?} */
MdDatepicker.prototype._ngZone;
/** @type {?} */
MdDatepicker.prototype._viewContainerRef;
/** @type {?} */
MdDatepicker.prototype._dateAdapter;
/** @type {?} */
MdDatepicker.prototype._dir;
/** @type {?} */
MdDatepicker.prototype._document;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
